/**
 * Aufgabentext aus einem Anruf — die EINE Stelle dafuer.
 *
 * Gemeldet am 24.08.2026 von Marlene (#32): in einem einzigen Aufgabentext
 * standen zwei Schreibweisen derselben Person.
 *
 *     Niklas Mehrer erkundigte sich nach einer Wohnungsbuchung …
 *     Anrufer: Niclas Mährer
 *
 * Die Zusammenfassung kommt von Sipgate (`routes/webhook.py`, das Feld wird
 * unveraendert gespeichert) — auf ihre Schreibweise haben wir keinen Einfluss,
 * Paul im Thread: „das transkript kommt von Sipgate, da haben wir keinen
 * Einfluss drauf". Die Zeile „Anrufer:" dagegen kommt aus dem zugeordneten
 * HERO-Kontakt und ist die gepflegte, richtige Schreibweise.
 *
 * 🔴 Bewusst NICHT: den Text von Sipgate umschreiben. Namen per Aehnlichkeit
 * im Fliesstext zu ersetzen trifft irgendwann den Falschen — und dann steht in
 * der Aufgabe eine Behauptung, die niemand gepruefst hat. Stattdessen wird der
 * gepflegte Name als der maßgebliche ausgewiesen und dazugesagt, dass die
 * Schreibweise im Text davon abweichen kann. Genau das war Marlenes zweiter
 * Vorschlag: „dann vermerkt machen, dass schreibweise unsicher ist vllt?!"
 */

/** Vergleichsform: Kleinschreibung, Umlaute aufgeloest, ohne Satzzeichen. */
function vergleichbar(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
}

/**
 * Steht der gepflegte Name **wortwoertlich** im Text?
 *
 * Nur die exakte Schreibweise zaehlt. „Niclas Mährer" gegen „Niklas Mehrer"
 * ist genau der Fall, der den Hinweis ausloesen soll — beide klingen gleich,
 * geschrieben sind sie es nicht.
 */
export function nameStehtWoertlichDrin(name: string, text: string): boolean {
  const n = (name || "").trim()
  if (!n || !text) return false
  return text.includes(n)
}

/**
 * Schreibabstand zweier Woerter (Levenshtein).
 *
 * 🔴 Ein Praefix-Vergleich reicht hier NICHT: „Mährer" wird zu „maehrer",
 * „Mehrer" zu „mehrer" — schon der zweite Buchstabe geht auseinander, obwohl
 * beide Formen dieselbe Person meinen. Genau der gemeldete Fall.
 */
function abstand(a: string, b: string): number {
  if (a === b) return 0
  const zeile = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    let voriges = zeile[0]
    zeile[0] = i
    for (let j = 1; j <= b.length; j++) {
      const gemerkt = zeile[j]
      zeile[j] = Math.min(
        zeile[j] + 1,
        zeile[j - 1] + 1,
        voriges + (a[i - 1] === b[j - 1] ? 0 : 1),
      )
      voriges = gemerkt
    }
  }
  return zeile[b.length]
}

/**
 * Weicht die Schreibweise im Text ab? True nur, wenn der Text die Person
 * erkennbar meint — also ein Wort enthaelt, das dem Nachnamen nahe kommt —,
 * sie aber anders schreibt.
 *
 * Bewusst eng gefasst: ohne die Naehe-Bedingung stuende der Hinweis unter
 * jeder Aufgabe, auch wo es nichts zu vergleichen gibt, und waere nach drei
 * Tagen unsichtbar.
 */
export function schreibweiseWeichtAb(name: string, text: string): boolean {
  const n = (name || "").trim()
  if (!n || !text) return false
  if (nameStehtWoertlichDrin(n, text)) return false
  const nachname = vergleichbar(n).split(" ").filter(Boolean).pop() || ""
  if (nachname.length < 4) return false
  // Kurze Namen vertragen nur einen Tippfehler Abstand, laengere zwei.
  const erlaubt = nachname.length <= 5 ? 1 : 2
  return vergleichbar(text)
    .split(" ")
    .some((w) => w.length >= 4 && abstand(w, nachname) <= erlaubt)
}

/**
 * Reiner Prozess-Satz ohne eigenen Inhalt — „Die Nachricht wurde notiert und
 * weitergeleitet." (#46, drittes Vorkommen 27.08.: solche Sätze standen 1:1 in
 * der Aufgabe für den Kunden).
 *
 * 🔴 Bewusst eng: gefiltert wird NUR der ganze Satz, der ausschließlich
 * beschreibt, dass eine Nachricht notiert/weitergegeben wurde. Sätze, in denen
 * „Nachricht" Teil des Anliegens ist, bleiben wortwörtlich stehen — dieselbe
 * Linie wie die harte Grenze in `_create_hero_task`: lieber eine kleine,
 * verlässliche Grenze als eine große, die echte Aufgabentexte zerlegt.
 */
const PROZESS_SATZ_RE =
  /^(?:die|eine)?\s*nachricht\s+wurde\s+(?:notiert|hinterlassen|vermerkt|aufgenommen)(?:\s+und\s+(?:weitergeleitet|weitergegeben))?\s*$/i

function istProzessSatz(satz: string): boolean {
  return PROZESS_SATZ_RE.test(satz.replace(/^[•\-*\s]+/, "").replace(/[.!]\s*$/, "").trim())
}

/** Entfernt reine Prozess-Sätze aus der Zusammenfassung, Zeile für Zeile. */
export function ohneProzessNotizen(summary: string): string {
  return summary
    .split("\n")
    .map((zeile) => {
      const saetze = zeile.split(/(?<=[.!?])\s+/).filter((s) => !istProzessSatz(s))
      return saetze.join(" ")
    })
    .filter((zeile) => zeile.replace(/^[•\-*\s]+/, "").trim().length > 0)
    .join("\n")
}

export function anrufAufgabenText(opts: {
  headline?: string | null
  summary?: string | null
  kontaktName?: string | null
  telefon?: string | null
}): string {
  const { headline, kontaktName, telefon } = opts
  const summary = opts.summary ? ohneProzessNotizen(opts.summary) : opts.summary
  const name = (kontaktName || "").trim()
  const nummer = (telefon || "").trim()
  const anrufer = name || nummer || "unbekannt"
  const hinweis =
    kontaktName && summary && schreibweiseWeichtAb(kontaktName, summary)
      ? " — im Text von Sipgate teils anders geschrieben, maßgeblich ist der Kontakt"
      : ""
  return [
    headline ? `Anliegen: ${headline}` : null,
    summary ? `Zusammenfassung:\n${summary}` : null,
    // #58: der Name verdraengte die Nummer — beide ausgeben, sonst muss der
    // Monteur die Rueckrufnummer aus der Anrufliste heraussuchen.
    `Anrufer: ${anrufer}${hinweis}` + (name && nummer ? `\nRückruf: ${nummer}` : ""),
  ].filter(Boolean).join("\n\n")
}
